import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { brand } from "@mbb/config";
import BottomNav from "./BottomNav";
import { useAuth } from "../lib/auth";

const PORTAL_TABS = [
  { to: "/portal", label: "Reading", end: true },
  { to: "/portal/discussion", label: "Discussion" },
  { to: "/portal/events", label: "Events" },
  { to: "/portal/members", label: "Members" },
  { to: "/portal/profile", label: "Profile" },
];

export default function Header() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  // Reserve space at the bottom of the viewport for the mobile bottom nav.
  useEffect(() => {
    if (!user) return;
    document.body.classList.add("has-bottom-nav");
    return () => document.body.classList.remove("has-bottom-nav");
  }, [user]);

  async function onSignOut() {
    setMenuOpen(false);
    await logout();
    navigate("/", { replace: true });
  }

  return (
    <>
      <header className="app-header">
        <div
          className="container"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBlock: "0.85rem",
            gap: "1rem",
          }}
        >
          <Link
            to={user ? "/portal" : "/"}
            aria-label={`${brand.name} home`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              flex: "0 0 auto",
            }}
          >
            <img
              src="/brand/wordmark.png"
              alt={brand.name}
              width={56}
              height={56}
              style={{
                height: "56px",
                width: "auto",
                display: "block",
              }}
            />
          </Link>

          {/* Desktop nav — shows everything in one row */}
          <nav className="nav-desktop" aria-label="Primary">
            {loading ? null : user ? (
              <SignedInNav onSignOut={onSignOut} isAdmin={user.role === "ADMIN"} />
            ) : (
              <SignedOutNav />
            )}
          </nav>

          {/* Mobile hamburger trigger */}
          <button
            type="button"
            className="nav-mobile-btn"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen(true)}
          >
            <HamburgerIcon />
          </button>
        </div>
      </header>

      <div
        id="mobile-menu"
        className={`nav-mobile-panel${menuOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        hidden={!menuOpen}
      >
        <div className="nav-mobile-panel__header">
          <button
            type="button"
            className="nav-mobile-btn"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <nav className="nav-mobile-panel__list" aria-label="Primary">
          {loading ? null : user ? (
            <>
              {/* Reading/Discussion/Events/Members live in BottomNav on mobile —
                  the hamburger holds the rest. */}
              <NavLink to="/portal/profile">profile</NavLink>
              {user.role === "ADMIN" && (
                <NavLink to="/admin/applications" className="accent">
                  applications
                </NavLink>
              )}
              <button type="button" onClick={onSignOut}>
                sign out
              </button>
            </>
          ) : (
            <>
              <a href="/#about" onClick={() => setMenuOpen(false)}>
                the club
              </a>
              <a href="/#join" onClick={() => setMenuOpen(false)}>
                how it works
              </a>
              <a href="/#faq" onClick={() => setMenuOpen(false)}>
                faq
              </a>
              <Link to="/login">sign in</Link>
            </>
          )}
        </nav>
      </div>

      {/* App-style bottom nav — mobile only, signed in only (CSS-gated). */}
      {user && <BottomNav />}
    </>
  );
}

function SignedOutNav() {
  return (
    <>
      <a href="/#about" className="eyebrow" style={{ color: "var(--c-palm)" }}>
        The club
      </a>
      <a href="/#join" className="eyebrow" style={{ color: "var(--c-palm)" }}>
        How it works
      </a>
      <a href="/#faq" className="eyebrow" style={{ color: "var(--c-palm)" }}>
        FAQ
      </a>
      <Link to="/login" className="eyebrow" style={{ color: "var(--c-palm)" }}>
        Sign in
      </Link>
    </>
  );
}

function SignedInNav({
  onSignOut,
  isAdmin,
}: {
  onSignOut: () => void;
  isAdmin: boolean;
}) {
  return (
    <>
      {PORTAL_TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          style={({ isActive }) => tabStyle(isActive, false)}
        >
          {t.label}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink
          to="/admin/applications"
          style={({ isActive }) => tabStyle(isActive, true)}
        >
          Applications
        </NavLink>
      )}
      <button
        type="button"
        onClick={onSignOut}
        className="btn btn--ghost"
        style={{ padding: "0.55rem 1rem", fontSize: "0.72rem", marginLeft: "0.25rem" }}
      >
        Sign out
      </button>
    </>
  );
}

function tabStyle(isActive: boolean, isAdmin: boolean): React.CSSProperties {
  const accent = isAdmin ? "var(--c-coral)" : "var(--c-palm)";
  return {
    fontFamily: "var(--f-label)",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontSize: "0.72rem",
    fontWeight: 600,
    padding: "0.45rem 0.85rem",
    borderRadius: "var(--r-pill)",
    background: isActive ? "var(--c-palm)" : "transparent",
    color: isActive ? "var(--c-sand)" : accent,
    whiteSpace: "nowrap",
  };
}

function HamburgerIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
